/**
 * /dashboard/analytics — G19 HR analytics dashboard.
 *
 * HR-admin only — the page hard role-gates to match the backend RBAC
 * (RequireRole(company_admin, hr_manager) on /analytics/dashboard).
 *
 * Window: defaults to "first day of current FY → today" with monthly
 * buckets. The Indian financial year runs Apr→Mar; we anchor the
 * default `from` at 1 April of the current FY.
 *
 * Six KPI sections (headcount / attrition / leave / training /
 * helpdesk / survey) — each rendered in its own card. The backend
 * computes sections independently and reports failures in `errors[]`;
 * we surface an inline error stripe per-card rather than hide the
 * entire page on a partial failure.
 *
 * Charting: no chart library (recharts isn't installed and CLAUDE.md
 * forbids new top-level deps). We render pure-CSS bar charts via
 * Tailwind flexbox + percentage heights, indigo-themed.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/analytics/page.tsx. Swaps:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link
 *   - analyticsApi remains the same import path
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  BarChart2,
  GraduationCap,
  HeartHandshake,
  LifeBuoy,
  TrendingDown,
  Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { analyticsApi } from "@/lib/api";
import type {
  AnalyticsBucket,
  AnalyticsDashboardResponse,
  HeadcountBucket,
} from "@/lib/api/analytics";
import { PageHeader } from "@/components/shared/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/settings/analytics")({
  component: AnalyticsDashboardPage,
});

const SECTION_KEYS = [
  "headcount",
  "attrition",
  "leave",
  "training",
  "helpdesk",
  "survey",
] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

const DAY_LABELS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

/** YYYY-MM-DD for the first day of the current Indian financial year. */
function fyStart(): string {
  const now = new Date();
  const year =
    now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-04-01`;
}

function todayISO(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/** Color-coded eNPS: green ≥ 50, amber 0..49, red < 0. */
function enpsClass(score: number): string {
  if (score >= 50) return "text-emerald-600";
  if (score >= 0) return "text-amber-600";
  return "text-red-600";
}

interface SectionCardProps {
  title: string;
  icon: typeof BarChart2;
  description?: string;
  href?: string;
  hrefLabel?: string;
  hasError?: boolean;
  children: React.ReactNode;
}

function SectionCard({
  title,
  icon: Icon,
  description,
  href,
  hrefLabel = "View module",
  hasError,
  children,
}: Readonly<SectionCardProps>) {
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-accent-foreground">
              <Icon className="h-4 w-4" />
            </span>
            <div>
              <CardTitle className="text-base">{title}</CardTitle>
              {description && (
                <CardDescription className="mt-0.5 text-xs">
                  {description}
                </CardDescription>
              )}
            </div>
          </div>
          {href && (
            <Link
              to={href}
              className="inline-flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
            >
              {hrefLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 pt-0">
        {hasError ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>
              This section is temporarily unavailable. Try again in a
              few minutes or narrow the date range.
            </p>
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

/** CSS bar-chart row: a label + a flex-grown bar whose width is value/max. */
function CSSBarRow({
  label,
  value,
  max,
  formatted,
}: Readonly<{
  label: string;
  value: number;
  max: number;
  formatted: string;
}>) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 py-1 text-xs">
      <span className="w-24 shrink-0 truncate text-muted-foreground">
        {label}
      </span>
      <div className="flex h-2 flex-1 overflow-hidden rounded bg-muted">
        <div
          className="h-full rounded bg-primary"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-12 shrink-0 text-right tabular-nums text-foreground">
        {formatted}
      </span>
    </div>
  );
}

/** CSS column-chart (vertical bars): a row of equal-width columns. */
function CSSColumns({
  data,
  formatValue,
}: Readonly<{
  data: Array<{ key: string; label: string; value: number }>;
  formatValue?: (v: number) => string;
}>) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex items-end gap-1.5">
      {data.map((d) => {
        const pct = Math.max(4, Math.round((d.value / max) * 100));
        return (
          <div
            key={d.key}
            className="flex min-w-0 flex-1 flex-col items-center gap-1"
            title={`${d.label}: ${
              formatValue ? formatValue(d.value) : d.value
            }`}
          >
            <div className="flex h-20 w-full items-end">
              <div
                className="w-full rounded-t bg-primary/80"
                style={{ height: `${pct}%` }}
              />
            </div>
            <span className="truncate text-[10px] text-muted-foreground">
              {d.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function netDeltaClass(delta: number): string {
  if (delta > 0) return "text-emerald-600";
  if (delta < 0) return "text-red-600";
  return "text-muted-foreground";
}

function HeadcountSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["headcount"];
  hasError: boolean;
}>) {
  const buckets: HeadcountBucket[] = data?.buckets ?? [];
  const delta = data ? data.new_hires - data.exits : 0;
  return (
    <SectionCard
      title="Headcount"
      icon={Users}
      description="Active employees + bucketed trend"
      href="/app/$tenantSlug/people"
      hrefLabel="People"
      hasError={hasError}
    >
      <div className="mb-3 flex items-baseline gap-3">
        <p className="text-3xl font-semibold tabular-nums">
          {data?.active_as_of_to ?? 0}
        </p>
        <p
          className={cn(
            "text-sm tabular-nums",
            netDeltaClass(delta),
          )}
        >
          {delta > 0 ? "+" : ""}
          {delta} net
        </p>
      </div>
      {buckets.length > 0 ? (
        <CSSColumns
          data={buckets.map((b) => ({
            key: b.bucket,
            label: b.bucket.slice(-2),
            value: b.count,
          }))}
        />
      ) : (
        <p className="text-xs text-muted-foreground">No bucket data.</p>
      )}
    </SectionCard>
  );
}

function AttritionSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["attrition"];
  hasError: boolean;
}>) {
  const reasons = data?.by_reason ?? [];
  const max = Math.max(1, ...reasons.map((r) => r.count));
  return (
    <SectionCard
      title="Attrition"
      icon={TrendingDown}
      description="Voluntary rate + exit reasons"
      href="/app/$tenantSlug/hr/offboarding"
      hrefLabel="Offboarding"
      hasError={hasError}
    >
      <div className="mb-3">
        <p className="text-3xl font-semibold tabular-nums">
          {data ? formatBps(data.voluntary_bps) : "0.00%"}
        </p>
        <p className="text-xs text-muted-foreground">Voluntary rate</p>
      </div>
      {reasons.length > 0 ? (
        <div className="space-y-0.5">
          {reasons.slice(0, 5).map((r) => (
            <CSSBarRow
              key={r.reason}
              label={r.reason}
              value={r.count}
              max={max}
              formatted={String(r.count)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No exits in window.</p>
      )}
    </SectionCard>
  );
}

function LeaveSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["leave"];
  hasError: boolean;
}>) {
  const byType = (data?.by_type ?? [])
    .slice()
    .sort((a, b) => b.total_days - a.total_days);
  const top5 = byType.slice(0, 5);
  const max = Math.max(1, ...top5.map((r) => r.total_days));

  // Backfill the 7-day-of-week array so empty days still render a column.
  const dowMap = new Map<number, number>();
  for (const r of data?.day_of_week ?? []) {
    dowMap.set(r.day_of_week, r.count);
  }
  const dowData = DAY_LABELS.map((label, i) => ({
    key: `dow-${i}`,
    label,
    value: dowMap.get(i) ?? 0,
  }));

  return (
    <SectionCard
      title="Leave heat map"
      icon={HeartHandshake}
      description="Top types + day-of-week distribution"
      href="/app/$tenantSlug/leave"
      hrefLabel="Leave"
      hasError={hasError}
    >
      {top5.length > 0 ? (
        <div className="mb-3 space-y-0.5">
          {top5.map((r) => (
            <CSSBarRow
              key={r.leave_type}
              label={r.leave_type}
              value={r.total_days}
              max={max}
              formatted={`${r.total_days}d`}
            />
          ))}
        </div>
      ) : (
        <p className="mb-3 text-xs text-muted-foreground">
          No leave taken in window.
        </p>
      )}
      <CSSColumns data={dowData} />
    </SectionCard>
  );
}

function TrainingSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["training"];
  hasError: boolean;
}>) {
  return (
    <SectionCard
      title="Training compliance"
      icon={GraduationCap}
      description="POSH + other module completion"
      href="/app/$tenantSlug/posh/compliance"
      hrefLabel="POSH compliance"
      hasError={hasError}
    >
      <div className="mb-3">
        <p className="text-3xl font-semibold tabular-nums">
          {data ? `${data.overall_completion_pct.toFixed(1)}%` : "0.0%"}
        </p>
        <p className="text-xs text-muted-foreground">Overall completion</p>
      </div>
      {data && data.below_threshold.length > 0 ? (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Below threshold
          </p>
          <ul className="space-y-1">
            {data.below_threshold.slice(0, 4).map((row) => (
              <li
                key={row.module_id}
                className="flex items-baseline justify-between text-xs"
              >
                <span className="truncate text-foreground">
                  {row.module_name}
                </span>
                <span className="ml-2 shrink-0 tabular-nums text-amber-600">
                  {row.completion_pct.toFixed(1)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Every module is at or above the 80% threshold.
        </p>
      )}
    </SectionCard>
  );
}

function HelpdeskSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["helpdesk"];
  hasError: boolean;
}>) {
  const open = data?.open ?? 0;
  const resolved = data?.resolved ?? 0;
  const total = Math.max(1, open + resolved);
  const openPct = Math.round((open / total) * 100);
  const resolvedPct = 100 - openPct;
  return (
    <SectionCard
      title="Helpdesk SLA"
      icon={LifeBuoy}
      description="Avg resolution + open vs resolved"
      href="/app/$tenantSlug/helpdesk"
      hrefLabel="Helpdesk"
      hasError={hasError}
    >
      <div className="mb-3">
        <p className="text-3xl font-semibold tabular-nums">
          {data ? `${data.avg_resolution_hours.toFixed(1)}h` : "0.0h"}
        </p>
        <p className="text-xs text-muted-foreground">
          Avg resolution time
        </p>
      </div>
      <div className="space-y-1.5">
        <div className="flex h-2 overflow-hidden rounded bg-muted">
          <div
            className="h-full bg-amber-500"
            style={{ width: `${openPct}%` }}
          />
          <div
            className="h-full bg-emerald-500"
            style={{ width: `${resolvedPct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            <span className="text-muted-foreground">Open</span>
            <span className="ml-1 tabular-nums text-foreground">
              {open}
            </span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-muted-foreground">Resolved</span>
            <span className="ml-1 tabular-nums text-foreground">
              {resolved}
            </span>
          </span>
        </div>
      </div>
    </SectionCard>
  );
}

function SurveySectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["survey"];
  hasError: boolean;
}>) {
  const enps = data?.enps ?? null;
  return (
    <SectionCard
      title="Survey participation"
      icon={BarChart2}
      description="eNPS + active surveys"
      href="/app/$tenantSlug/me/surveys"
      hrefLabel="Surveys"
      hasError={hasError}
    >
      <div className="mb-3 flex items-baseline gap-3">
        <p
          className={cn(
            "text-3xl font-semibold tabular-nums",
            enps !== null ? enpsClass(enps) : "text-muted-foreground",
          )}
        >
          {enps !== null ? enps : "—"}
        </p>
        <p className="text-xs text-muted-foreground">
          {enps !== null ? "Latest eNPS" : "No completed survey"}
        </p>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Active surveys</span>
        <span className="tabular-nums text-foreground">
          {data?.active_surveys ?? 0}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Responses</span>
        <span className="tabular-nums text-foreground">
          {data?.responses ?? 0}
        </span>
      </div>
    </SectionCard>
  );
}

function LoadingGrid() {
  return (
    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {SECTION_KEYS.map((k) => (
        <Skeleton key={k} className="h-56" />
      ))}
    </div>
  );
}

function AnalyticsDashboardPage() {
  const { user } = useAuth();
  const allowed = isHRAdmin(user?.role);

  const [from, setFrom] = useState<string>(() => fyStart());
  const [to, setTo] = useState<string>(() => todayISO());
  const buckets: AnalyticsBucket = "monthly";

  const query = useQuery({
    queryKey: ["analytics", "dashboard", from, to, buckets] as const,
    queryFn: ({ signal }) =>
      analyticsApi.getAnalyticsDashboard(from, to, buckets, signal),
    enabled: allowed && Boolean(from) && Boolean(to) && from <= to,
    staleTime: 60 * 1000,
  });

  const errorSet = useMemo(() => {
    const out = new Set<SectionKey>();
    for (const k of query.data?.errors ?? []) {
      if ((SECTION_KEYS as readonly string[]).includes(k)) {
        out.add(k as SectionKey);
      }
    }
    return out;
  }, [query.data?.errors]);

  if (!allowed) {
    return (
      <>
        <PageHeader title="Analytics" />
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <BarChart2 className="h-6 w-6" />
            </span>
            <p className="text-sm text-muted-foreground">
              The analytics dashboard is restricted to HR admins. Ask
              your company admin or HR manager for access.
            </p>
          </CardContent>
        </Card>
      </>
    );
  }

  const data = query.data;

  return (
    <>
      <PageHeader
        title="Analytics"
        description="Headcount, attrition, leave, training, helpdesk, surveys."
      />

      {/* Window picker */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="analytics-from"
              className="text-xs font-medium text-muted-foreground"
            >
              From
            </label>
            <input
              id="analytics-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="analytics-to"
              className="text-xs font-medium text-muted-foreground"
            >
              To
            </label>
            <input
              id="analytics-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              Buckets
            </span>
            <span className="inline-flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm">
              Monthly
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => {
              setFrom(fyStart());
              setTo(todayISO());
            }}
          >
            Reset to FY-to-date
          </Button>
        </CardContent>
      </Card>

      {from > to && (
        <Card className="mb-4 border-red-300 bg-red-50">
          <CardContent className="py-3 text-sm text-red-900">
            The “From” date must be on or before the “To” date.
          </CardContent>
        </Card>
      )}

      {query.isPending && from <= to ? (
        <LoadingGrid />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <HeadcountSectionView
            data={data?.headcount}
            hasError={errorSet.has("headcount")}
          />
          <AttritionSectionView
            data={data?.attrition}
            hasError={errorSet.has("attrition")}
          />
          <LeaveSectionView
            data={data?.leave}
            hasError={errorSet.has("leave")}
          />
          <TrainingSectionView
            data={data?.training}
            hasError={errorSet.has("training")}
          />
          <HelpdeskSectionView
            data={data?.helpdesk}
            hasError={errorSet.has("helpdesk")}
          />
          <SurveySectionView
            data={data?.survey}
            hasError={errorSet.has("survey")}
          />
        </div>
      )}

      {data?.generated_at && (
        <p className="mt-4 text-right text-xs text-muted-foreground">
          Snapshot generated at{" "}
          {new Date(data.generated_at).toLocaleString()}
        </p>
      )}
    </>
  );
}
